import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  RefreshCw, Send, Search, CheckCircle2, XCircle, Clock, AlertCircle,
} from "lucide-react";

/** Admin view of outbound XPay webhook deliveries with single-click replay. */
const STATUS_TONE = {
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:    "bg-rose-50 text-rose-700 border-rose-200",
  skipped:   "bg-slate-100 text-slate-700 border-slate-200",
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
};
const STATUS_ICON = {
  delivered: CheckCircle2, failed: XCircle, skipped: AlertCircle, pending: Clock,
};

const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function AdminXpayWebhookLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");      // '' | 'delivered' | 'failed' | 'skipped'
  const [merchantFilter, setMerchantFilter] = useState("");  // merchant id (free-text)
  const [replayingId, setReplayingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter)   params.set("status_filter", statusFilter);
      if (merchantFilter) params.set("merchant_id", merchantFilter.trim());
      const r = await api.get(`/admin/xpay/webhook-logs?${params.toString()}`);
      setLogs(r.data?.logs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load webhook logs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const replay = async (log) => {
    setReplayingId(log.id);
    try {
      const r = await api.post(`/admin/xpay/webhook-logs/${log.id}/replay`);
      const fresh = r.data?.log;
      const ok = fresh?.status === "delivered";
      if (ok) toast.success(`Replay delivered (HTTP ${fresh.last_status})`);
      else    toast.error(`Replay failed: ${fresh?.last_error || "unknown"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replay request failed");
    } finally { setReplayingId(null); }
  };

  return (
    <AdminLayout
      title="XPay webhook logs"
      subtitle="Every outbound merchant-webhook delivery, with single-click replay against the merchant's current secret + URL."
      actions={
        <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-webhook-logs-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      }
    >
      {/* Filter row */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="xpay-webhook-logs-filters">
        {[
          { v: "",          l: "All" },
          { v: "delivered", l: "Delivered" },
          { v: "failed",    l: "Failed" },
          { v: "skipped",   l: "Skipped (no URL)" },
        ].map((f) => (
          <button key={f.l}
                  onClick={() => setStatusFilter(f.v)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors
                    ${statusFilter === f.v
                      ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                      : "bg-background border-border text-foreground hover:bg-muted"}`}
                  data-testid={`xpay-webhook-logs-tab-${f.v || "all"}`}>
            {f.l}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={merchantFilter}
                    onChange={(e) => setMerchantFilter(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && load()}
                    placeholder="Filter by merchant id…"
                    className="pl-9 h-9 w-72 text-xs rounded-xl"
                    data-testid="xpay-webhook-logs-merchant-filter" />
          </div>
          <Button onClick={load} size="sm" className="rounded-xl" data-testid="xpay-webhook-logs-apply">
            Apply
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="xch-card overflow-hidden" data-testid="xpay-webhook-logs-table">
        {loading && <div className="p-6 text-xs text-muted-foreground">Loading…</div>}
        {!loading && logs.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No webhook deliveries{statusFilter ? ` with status "${statusFilter}"` : ""} yet.
          </div>
        )}
        {!loading && logs.length > 0 && (
          <div className="divide-y divide-border">
            {logs.map((l) => {
              const Icon = STATUS_ICON[l.status] || Clock;
              const canReplay = l.status !== "skipped";  // skipped means no URL — nothing to retry
              return (
                <div key={l.id} className="px-5 py-3.5 flex items-center gap-3 flex-wrap"
                      data-testid={`xpay-webhook-row-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-mono text-[11px] font-bold text-emerald-700">{l.event || "—"}</code>
                      <Badge className={`text-[10px] uppercase tracking-wider gap-1 ${STATUS_TONE[l.status] || ""}`}
                              variant="outline" data-testid={`xpay-webhook-status-${l.id}`}>
                        <Icon className="w-3 h-3" /> {l.status}
                      </Badge>
                      {typeof l.last_status === "number" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted font-mono">
                          HTTP {l.last_status}
                        </span>
                      )}
                      {typeof l.attempts === "number" && l.attempts > 1 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted font-mono">
                          {l.attempts} attempts
                        </span>
                      )}
                      {l.replayed_from && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono"
                               title={`Replay of ${l.replayed_from}`}>
                          replay
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate" title={l.url}>
                      {l.url || "no callback URL configured"}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      link={l.link_id || "—"} · merchant={l.merchant_id?.slice(0, 8) || "—"} · {fmt(l.last_attempt_at || l.created_at)}
                    </div>
                    {l.last_error && (
                      <div className="text-[10px] text-rose-700 mt-1 truncate" title={l.last_error}>
                        Error: {l.last_error}
                      </div>
                    )}
                  </div>
                  <Button onClick={() => replay(l)}
                           disabled={!canReplay || replayingId === l.id}
                           variant="outline" size="sm"
                           className="rounded-xl gap-1.5"
                           title={canReplay ? "Re-dispatch using the merchant's current secret + URL"
                                              : "Skipped deliveries have no URL to retry"}
                           data-testid={`xpay-webhook-replay-${l.id}`}>
                    <Send className="w-3.5 h-3.5" />
                    {replayingId === l.id ? "Replaying…" : "Replay"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
